import { useState } from "react";
export type ScopedProjectTemplatesScopeKey = string;
export interface ChangedProjectTemplatesScope {
    guid: string;
    projectId: string;
    templateId: string;
    tenantId: string;
    environmentIds: string[];
    status: "added" | "modified" | "deleted";
}
export interface ScopedProjectTemplatesScopeChangeTracker {
    has(key: ScopedProjectTemplatesScopeKey): boolean;
    get(key: ScopedProjectTemplatesScopeKey): string[] | undefined;
    set(key: ScopedProjectTemplatesScopeKey, value: string[]): void;
    add(key: ScopedProjectTemplatesScopeKey, value: string[]): void;
    delete(key: ScopedProjectTemplatesScopeKey): void;
    reset(): void;
    hasAny(): boolean;
    values(): ChangedProjectTemplatesScope[];
    changedTenants(): Set<string>;
    newVariables(projectId: string, templateId: string, tenantId: string): Map<ScopedProjectTemplatesScopeKey, string[]>;
    removedVariables(): Set<ScopedProjectTemplatesScopeKey>;
}
export function extractProjectTemplatesValueIds(key: ScopedProjectTemplatesScopeKey): {
    projectId: string;
    templateId: string;
    tenantId: string;
    guid: string;
} {
    const parts = key.split(";");
    return {
        projectId: parts[0],
        templateId: parts[1],
        tenantId: parts[2],
        guid: parts[4],
    };
}
export function createScopedProjectTemplatesScopeKey(projectId: string, templateId: string, tenantId: string, guid: string): ScopedProjectTemplatesScopeKey {
    return `${projectId};${templateId};${tenantId};${guid}`;
}
export function useScopedProjectTemplatesScopeChangeTracker(): ScopedProjectTemplatesScopeChangeTracker {
    const [changes, setChanges] = useState<Map<ScopedProjectTemplatesScopeKey, {
        value: string[];
        status: "added" | "modified" | "deleted";
    }>>(new Map());
    const has = (key: ScopedProjectTemplatesScopeKey): boolean => {
        return changes.has(key);
    };
    const get = (key: ScopedProjectTemplatesScopeKey): string[] | undefined => {
        return changes.get(key)?.value;
    };
    const set = (key: ScopedProjectTemplatesScopeKey, value: string[]): void => {
        setChanges((prev) => {
            const newChanges = new Map(prev);
            const existing = prev.get(key);
            const status = existing?.status === "added" ? "added" : "modified";
            newChanges.set(key, { value, status });
            return newChanges;
        });
    };
    const add = (key: ScopedProjectTemplatesScopeKey, value: string[]): void => {
        setChanges((prev) => {
            const newChanges = new Map(prev);
            newChanges.set(key, { value, status: "added" });
            return newChanges;
        });
    };
    const deleteKey = (key: ScopedProjectTemplatesScopeKey): void => {
        setChanges((prev) => {
            const newChanges = new Map(prev);
            const existing = prev.get(key);
            if (existing?.status === "added") {
                // If it was added, just remove it completely
                newChanges.delete(key);
            }
            else {
                // If it existed before, mark as deleted
                newChanges.set(key, { value: [], status: "deleted" });
            }
            return newChanges;
        });
    };
    const reset = (): void => {
        setChanges(new Map());
    };
    const hasAny = (): boolean => {
        return changes.size > 0;
    };
    const values = (): ChangedProjectTemplatesScope[] => {
        const result: ChangedProjectTemplatesScope[] = [];
        for (const [key, change] of changes) {
            const { projectId, templateId, tenantId, guid } = extractProjectTemplatesValueIds(key);
            result.push({
                guid,
                projectId,
                templateId,
                tenantId,
                environmentIds: change.value,
                status: change.status,
            });
        }
        return result;
    };
    const changedTenants = (): Set<string> => {
        const tenants = new Set<string>();
        for (const [key] of changes) {
            const { tenantId } = extractProjectTemplatesValueIds(key);
            tenants.add(tenantId);
        }
        return tenants;
    };
    const newVariables = (projectId: string, templateId: string, tenantId: string): Map<ScopedProjectTemplatesScopeKey, string[]> => {
        const result = new Map<ScopedProjectTemplatesScopeKey, string[]>();
        for (const [key, change] of changes) {
            const ids = extractProjectTemplatesValueIds(key);
            if (ids.projectId === projectId && ids.templateId === templateId && ids.tenantId === tenantId && change.status === "added") {
                result.set(key, change.value);
            }
        }
        return result;
    };
    const removedVariables = (): Set<ScopedProjectTemplatesScopeKey> => {
        const result = new Set<ScopedProjectTemplatesScopeKey>();
        for (const [key, change] of changes) {
            if (change.status === "deleted") {
                result.add(key);
            }
        }
        return result;
    };
    return {
        has,
        get,
        set,
        add,
        delete: deleteKey,
        reset,
        hasAny,
        values,
        changedTenants,
        newVariables,
        removedVariables,
    };
}
